import * as pc from 'playcanvas'
import type { NormalizedRun } from '../api/types'
import { useArenaStore } from '../store'
import type {
  HqAcceptanceFloor,
  HqAcceptanceViewId,
  HqAcceptanceViewPose,
  HqProgramTraversalArtifact,
  HqTraversalArtifact,
} from './hqAcceptanceHarness'
import type { HqRoomActionId, HqRoomActionPose } from '../pc/world/hqRoomActions'
import type {
  CampusAcceptancePose,
  CampusAcceptanceViewId,
  CampusDestinationViewId,
  CampusViewDiagnostics,
} from './campusAcceptance'
import type { CampusTraversalArtifact } from './campusAcceptance'
import type { WorkplaceTraversalArtifact } from './workplaceAcceptance'
import type { AssetZoneSnapshot } from '../pc/assetZones'
import type { DatacentreProjectionState } from '../pc/world/campusRooms'

export const DRAW_CALL_BUDGET = 250
export const DRAW_CALL_RUN_COUNT = 20
export const DRAW_CALL_SETTLE_FRAMES = 120
export const DRAW_CALL_SAMPLE_FRAMES = 120
export const DRAW_CALL_QUERY = 'drawCallHarness'
export const DRAW_CALL_SKIP_INITIAL_QUERY = 'skipInitialDrawCallArtifact'

export function isDrawCallHarnessEnabled(
  search = window.location.search,
): boolean {
  return new URLSearchParams(search).get(DRAW_CALL_QUERY) === '1'
}

export function shouldSkipInitialDrawCallArtifact(
  search = window.location.search,
): boolean {
  return new URLSearchParams(search).get(DRAW_CALL_SKIP_INITIAL_QUERY) === '1'
}

export interface DrawCallHarnessPose {
  x: number
  z: number
  yawDeg: number
}

export interface CampusTransitionObservation {
  plotId: 'P4' | 'P5' | 'P8'
  entryThreshold: { minX: number; maxX: number; minZ: number; maxZ: number }
  expectedInteriorSpawn: { x: number; z: number; yaw: number }
  observedInteriorSpawn: { x: number; y: number; z: number } | null
  entryPassed: boolean
  exitThreshold: { minX: number; maxX: number; minZ: number; maxZ: number }
  expectedStreetExit: { x: number; z: number; yaw: number }
  observedStreetExit: { x: number; y: number; z: number } | null
  exitPassed: boolean
  passed: boolean
  failures: string[]
}

export interface CampusTransitionArtifact {
  schema: 'arena.campus-transitions.v1'
  status: 'measured'
  capturedAt: string
  plots: CampusTransitionObservation[]
  gate: {
    expectedPlots: readonly ['P4', 'P5', 'P8']
    exactCoveragePass: boolean
    entryPass: boolean
    exitPass: boolean
    passed: boolean
  }
  passed: boolean
}

export interface DrawCallHarnessView {
  capture(): DrawCallHarnessPose
  showExterior(): void
  showCampusView?(id: CampusAcceptanceViewId): CampusAcceptancePose
  campusViewDiagnostics?(id: CampusDestinationViewId): CampusViewDiagnostics
  showWorkload(): void
  /** Enter any built plot interior using the same active-room ownership as gameplay. */
  showInterior?(plotId: string): void
  setWorkloadFixture?(enabled: boolean): void
  /** P7 truth-state proof without changing application ownership or fetching. */
  setLibraryKnowledgeFixture?(
    state: 'empty' | 'maximum' | 'stale' | 'restore'
  ): void
  /** P4 truth-state proof without changing Portal/world ownership. */
  setExchangeProjectionFixture?(
    state:
      | 'empty'
      | 'maximum'
      | 'loading'
      | 'stale'
      | 'disconnected'
      | 'restore'
  ): void
  exchangeProjectionFixtureDiagnostics?(): {
    status: ReturnType<typeof useArenaStore.getState>['remoteProjectionStatus']
    healthPresent: boolean
    runningRuns: number
    staleRuns: number
    queueDepth: number | null
    activeAgents: number | null
    poolSize: number | null
    totalCostUsd: number | null
    totalTokens: number | null
  }
  /** P5 truth-state proof without changing Portal/world ownership. */
  setWorkshopProjectionFixture?(
    state: 'empty' | 'maximum' | 'stale' | 'restore'
  ): void
  workshopProjectionFixtureDiagnostics?(): {
    status: ReturnType<typeof useArenaStore.getState>['remoteProjectionStatus']
    healthPresent: boolean
    runningRuns: number
    staleRuns: number
  }
  /** P6 roster/chat truth-state proof without changing Portal ownership. */
  setCafeProjectionFixture?(
    state:
      | 'empty'
      | 'maximum'
      | 'loading'
      | 'stale'
      | 'disconnected'
      | 'restore'
  ): void
  cafeProjectionFixtureDiagnostics?(): {
    status: ReturnType<typeof useArenaStore.getState>['remoteProjectionStatus']
    agents: number
    rawChats: number
    boundChats: number
    writableBoundChats: number
    unboundChats: number
    orphanChats: number
  }
  setDatacentreProjectionFixture?(
    state:
      | 'empty'
      | 'maximum'
      | 'loading'
      | 'stale'
      | 'disconnected'
      | 'restore'
  ): void
  datacentreProjectionFixtureDiagnostics?(): DatacentreProjectionState
  blockingColliderCountAtPose?(pose: { x: number; z: number }): number
  showFloor?(floor: HqAcceptanceFloor): HqAcceptanceViewPose
  showAcceptanceView?(id: HqAcceptanceViewId): HqAcceptanceViewPose
  showHqRoomAction?(id: HqRoomActionId): HqRoomActionPose
  /** TASK-1184 close, deterministic desk-rig acceptance view. */
  showSyntyCharacterProof?(): HqAcceptanceViewPose
  /** TASK-1192 scale gate, one deterministic production-floor view at a time. */
  showSyntyRosterFloor?(floor: 1 | 2): HqAcceptanceViewPose
  setSyntyCharacterFixture?(enabled: boolean): void
  setSyntyRosterScaleFixture?(options: {
    enabled: boolean
    reverse?: boolean
  }): void
  syntyCharacterDiagnostics?(): unknown
  syntyRosterLifecycleDiagnostics?(): unknown
  assetLoadDiagnostics?(files?: readonly string[]): unknown
  setSyntyCharacterState?(state: 'idle' | 'working' | 'waiting' | 'alarmed' | null): void
  runCampusTraversal?(): CampusTraversalArtifact
  /** Exercise the production threshold/fade/teleport path in both directions. */
  runCampusTransitions?(): Promise<CampusTransitionArtifact>
  runWorkplaceTraversal?(): WorkplaceTraversalArtifact
  assetZoneSnapshot?(): AssetZoneSnapshot
  runTraversal?(): HqTraversalArtifact
  runProgramTraversal?(): HqProgramTraversalArtifact
  restore(pose: DrawCallHarnessPose): void
}

export interface DrawCallArtifact {
  schema: 'arena.draw-calls.v1'
  status: 'measured'
  capturedAt: string
  runCount: number
  settleFrames: number
  sampleFrames: number
  budgetExclusive: number
  min: number
  median: number
  p95: number
  max: number
  passed: boolean
  /** Settled district-spawn view with no projected agents. */
  baseline?: number
  /** Same HQ camera as the workload, before the 20 stations are projected. */
  hqBaseline?: number
  breakdown?: Record<string, number>
  userAgent: string
}

export function createDrawCallRuns(
  capturedAt = '2026-07-24T00:00:00.000Z',
): NormalizedRun[] {
  return Array.from({ length: DRAW_CALL_RUN_COUNT }, (_, index) => ({
    id: `perf-run-${String(index + 1).padStart(2, '0')}`,
    taskId: `PERF-${String(index + 1).padStart(2, '0')}`,
    title: `Draw-call station ${index + 1}`,
    status: 'running',
    phases: [],
    activeAgents: 1,
    createdAt: capturedAt,
    updatedAt: capturedAt,
  }))
}

function percentile(sorted: readonly number[], ratio: number): number {
  const index = Math.min(
    sorted.length - 1,
    Math.max(0, Math.ceil(sorted.length * ratio) - 1),
  )
  return sorted[index]
}

export function summarizeDrawCalls(
  samples: readonly number[],
  capturedAt = new Date().toISOString(),
  userAgent = 'unknown',
  breakdown?: Record<string, number>,
  baseline?: number,
  hqBaseline?: number,
): DrawCallArtifact {
  if (samples.length === 0 || samples.some((value) =>
    !Number.isFinite(value) || value < 0)) {
    throw new Error('draw-call samples must be non-empty finite non-negative values')
  }
  const sorted = [...samples].sort((a, b) => a - b)
  // `baseline` is the settled exterior scenario while `samples` are captured
  // inside HQ with all 20 stations visible. The top-level gate represents the
  // worse of the two rather than allowing either viewpoint to hide the other.
  const max = Math.max(
    sorted[sorted.length - 1],
    baseline ?? 0,
    hqBaseline ?? 0,
  )
  return {
    schema: 'arena.draw-calls.v1',
    status: 'measured',
    capturedAt,
    runCount: DRAW_CALL_RUN_COUNT,
    settleFrames: DRAW_CALL_SETTLE_FRAMES,
    sampleFrames: samples.length,
    budgetExclusive: DRAW_CALL_BUDGET,
    min: sorted[0],
    median: percentile(sorted, 0.5),
    p95: percentile(sorted, 0.95),
    max,
    passed: max < DRAW_CALL_BUDGET,
    ...(baseline !== undefined ? { baseline } : {}),
    ...(hqBaseline !== undefined ? { hqBaseline } : {}),
    ...(breakdown ? { breakdown } : {}),
    userAgent,
  }
}

function nextFrame(signal?: AbortSignal): Promise<boolean> {
  if (signal?.aborted) return Promise.resolve(false)
  return new Promise((resolve) => {
    let settled = false
    const finish = (rendered: boolean): void => {
      if (settled) return
      settled = true
      signal?.removeEventListener('abort', onAbort)
      resolve(rendered)
    }
    const rafId = requestAnimationFrame(() => finish(true))
    const onAbort = (): void => {
      cancelAnimationFrame(rafId)
      finish(false)
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

declare global {
  interface Window {
    __ARENA_DRAW_CALL_RESULT__?: DrawCallArtifact
    __ARENA_DRAW_CALL_VIEW__?: DrawCallHarnessView
    __ARENA_DRAW_CALL_APP__?: pc.Application
  }
}

interface ActiveHarness {
  token: symbol
  signal?: AbortSignal
}

let activeHarness: ActiveHarness | undefined

function drawCallTotal(app: pc.Application): number {
  const total = app.stats?.drawCalls?.total
  if (!Number.isFinite(total) || total < 0) {
    throw new Error(
      'PlayCanvas draw-call stats are unavailable; no measurement artifact was produced',
    )
  }
  return total
}

function drawCallBreakdown(app: pc.Application): Record<string, number> {
  const stats = app.stats?.drawCalls as unknown
  if (!stats || typeof stats !== 'object') return {}
  return Object.fromEntries(
    Object.entries(stats)
      .filter((entry): entry is [string, number] =>
        Number.isFinite(entry[1]) && entry[1] >= 0)
      .sort(([a], [b]) => a.localeCompare(b)),
  )
}

function sceneBatchDiagnostics(app: pc.Application): Record<string, number> {
  const root = app.root as pc.Entity | undefined
  if (!root || typeof root.findComponents !== 'function') return {}
  const renders = root.findComponents('render') as pc.RenderComponent[]
  let meshInstances = 0
  let unbatchedMeshInstances = 0
  let unbatchedShadowCasters = 0
  let skinnedMeshInstances = 0
  const groupComponents = new Map<number, number>()
  const unbatchedFamilies = new Map<string, number>()
  const topLevelFamily = (entity: pc.Entity): string => {
    let current = entity
    while (
      current.parent
      && current.parent.parent
      && current.parent.parent !== app.root
    ) current = current.parent as pc.Entity
    const name = current.name
    if (name.startsWith('prop-')) return name.replace(/-\d+$/, '')
    if (name.startsWith('char-')) return name
    return name.replace(/-\d+$/, '')
  }
  for (const render of renders) {
    meshInstances += render.meshInstances.length
    if (render.batchGroupId < 0) {
      unbatchedMeshInstances += render.meshInstances.length
      const family = topLevelFamily(render.entity)
      unbatchedFamilies.set(
        family,
        (unbatchedFamilies.get(family) ?? 0) + render.meshInstances.length,
      )
      if (render.castShadows) {
        unbatchedShadowCasters += render.meshInstances.length
      }
    } else {
      groupComponents.set(
        render.batchGroupId,
        (groupComponents.get(render.batchGroupId) ?? 0) + 1,
      )
    }
    skinnedMeshInstances += render.meshInstances.filter(
      (mesh) => mesh.skinInstance != null,
    ).length
  }
  const diagnostics: Record<string, number> = {
    sceneRenderComponents: renders.length,
    sceneMeshInstances: meshInstances,
    sceneUnbatchedMeshInstances: unbatchedMeshInstances,
    sceneUnbatchedShadowCasters: unbatchedShadowCasters,
    sceneSkinnedMeshInstances: skinnedMeshInstances,
  }
  const batcher = app.batcher as unknown as {
    getGroupById?(id: number): { name?: string } | null
    getBatches?(id: number): unknown[]
  } | undefined
  for (const [id, components] of groupComponents) {
    const group = batcher?.getGroupById?.(id)
    const safeName = (group?.name ?? `group-${id}`)
      .replace(/[^A-Za-z0-9_-]+/g, '_')
    diagnostics[`batchComponents_${safeName}`] = components
    diagnostics[`batches_${safeName}`] =
      batcher?.getBatches?.(id).length ?? 0
  }
  for (const [name, count] of [...unbatchedFamilies]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 24)) {
    const safeName = name.replace(/[^A-Za-z0-9_-]+/g, '_')
    diagnostics[`unbatched_${safeName}`] = count
  }
  return diagnostics
}

/** Main-camera sphere test, intentionally independent of shadow submissions. */
export function meshInMainCameraFrustum(
  mesh: Pick<pc.MeshInstance, 'aabb' | 'visible'>,
  camera: Pick<pc.CameraComponent, 'frustum'>,
): boolean {
  if (mesh.visible === false) return false
  const half = mesh.aabb.halfExtents
  const sphere = new pc.BoundingSphere(
    mesh.aabb.center,
    Math.hypot(half.x, half.y, half.z),
  )
  return camera.frustum.containsSphere(sphere) !== 0
}

/**
 * Attribute authored and batched instances against the main camera's own
 * layer set and frustum. `visibleThisFrame` is shared across render passes and
 * can be true solely because a shadow camera submitted the mesh; it therefore
 * cannot prove that the player camera saw a zone.
 */
function sceneMainCameraFrustumDiagnostics(app: pc.Application): Record<string, number> {
  const scene = app.scene as pc.Scene | undefined
  const root = app.root as pc.Entity | undefined
  const cameraNode = root?.findByName?.('camera')
  const camera = cameraNode instanceof pc.Entity ? cameraNode.camera : undefined
  if (!scene?.layers || !root || !camera || typeof root.findComponents !== 'function') return {}

  const inFrustum = new Set<pc.MeshInstance>()
  for (const layerId of camera.layers) {
    const layer = scene.layers.getLayerById(layerId)
    if (!layer) continue
    for (const mesh of layer.meshInstances) {
      if (meshInMainCameraFrustum(mesh, camera)) inFrustum.add(mesh)
    }
  }

  const diagnostics: Record<string, number> = {
    mainCameraFrustumMeshInstances: inFrustum.size,
    mainCameraFrustumShadowCasters: [...inFrustum].filter((mesh) => mesh.castShadow).length,
    mainCameraFrustumSkinnedMeshInstances: [...inFrustum].filter(
      (mesh) => mesh.skinInstance != null,
    ).length,
  }

  const batcher = app.batcher as unknown as {
    getGroupById?(id: number): { name?: string } | null
    getBatches?(id: number): Array<{
      meshInstance?: pc.MeshInstance | null
    }>
  } | undefined
  const renders = root.findComponents('render') as pc.RenderComponent[]
  const groupIds = new Set(
    renders
      .map((render) => render.batchGroupId)
      .filter((id) => id >= 0),
  )
  for (const id of groupIds) {
    const group = batcher?.getGroupById?.(id)
    const safeName = (group?.name ?? `group-${id}`)
      .replace(/[^A-Za-z0-9_-]+/g, '_')
    const frustumBatches = batcher?.getBatches?.(id).filter(
      (batch) => batch.meshInstance && inFrustum.has(batch.meshInstance),
    ) ?? []
    if (frustumBatches.length > 0) {
      diagnostics[`mainCameraFrustumBatches_${safeName}`] = frustumBatches.length
      diagnostics[`mainCameraFrustumBatchShadowCasters_${safeName}`] = frustumBatches.filter(
        (batch) => batch.meshInstance?.castShadow,
      ).length
    }
  }

  const families = new Map<string, number>()
  const family = (entity: pc.Entity): string => {
    let current = entity
    while (
      current.parent
      && current.parent.parent
      && current.parent.parent !== root
    ) current = current.parent as pc.Entity
    const name = current.name
    if (name.startsWith('prop-')) return name.replace(/-\d+$/, '')
    if (name.startsWith('char-')) return name
    return name.replace(/-\d+$/, '')
  }
  for (const render of renders) {
    if (render.batchGroupId >= 0) continue
    const count = render.meshInstances.filter((mesh) => inFrustum.has(mesh)).length
    if (count === 0) continue
    const name = family(render.entity)
    families.set(name, (families.get(name) ?? 0) + count)
  }
  for (const [name, count] of [...families]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 32)) {
    const safeName = name.replace(/[^A-Za-z0-9_-]+/g, '_')
    diagnostics[`mainCameraFrustumUnbatched_${safeName}`] = count
  }
  return diagnostics
}

/**
 * Opt-in real renderer harness. CI/browser automation reads the artifact from
 * `window.__ARENA_DRAW_CALL_RESULT__` and persists it before running the
 * repository assertion script.
 */
export async function maybeRunDrawCallHarness(
  app: pc.Application,
  assetsSettled: Promise<unknown>,
  search = window.location.search,
  signal?: AbortSignal,
  view?: DrawCallHarnessView,
): Promise<DrawCallArtifact | null> {
  if (!isDrawCallHarnessEnabled(search)) return null
  window.__ARENA_DRAW_CALL_VIEW__ = view
  window.__ARENA_DRAW_CALL_APP__ = app
  if (shouldSkipInitialDrawCallArtifact(search)) return null
  if (activeHarness && !activeHarness.signal?.aborted) {
    console.warn('[draw-call-harness] ignored duplicate invocation')
    return null
  }
  const token = Symbol('draw-call-harness')
  activeHarness = { token, signal }

  let previous: ReturnType<typeof useArenaStore.getState> | undefined
  let runs: NormalizedRun[] | undefined
  let baselineRuns: NormalizedRun[] | undefined
  let previousPose: DrawCallHarnessPose | undefined
  try {
    await assetsSettled
    if (signal?.aborted) return null

    previous = useArenaStore.getState()
    previousPose = view?.capture()
    baselineRuns = []
    view?.showExterior()
    // Capture the exterior cost without projected agents, then move inside HQ
    // for the real 20-station workload. The former implementation stayed at
    // district spawn for both phases, so every station was a kilometre away
    // and frustum-culled; equal totals were a false-positive workload gate.
    for (let frame = 0; frame < 60; frame++) {
      useArenaStore.setState({ runs: baselineRuns, agents: [] })
      if (!(await nextFrame(signal))) return null
    }
    const baseline = drawCallTotal(app)
    view?.showWorkload()
    for (let frame = 0; frame < 60; frame++) {
      useArenaStore.setState({ runs: baselineRuns, agents: [] })
      if (!(await nextFrame(signal))) return null
    }
    const hqBaseline = drawCallTotal(app)
    runs = createDrawCallRuns(new Date().toISOString())
    const applyFixture = (): void => {
      const state = useArenaStore.getState()
      if (state.runs !== runs || state.agents.length !== 0) {
        useArenaStore.setState({ runs, agents: [] })
      }
    }

    for (let frame = 0; frame < DRAW_CALL_SETTLE_FRAMES; frame++) {
      if (signal?.aborted) return null
      applyFixture()
      if (!(await nextFrame(signal))) return null
    }
    const samples: number[] = []
    for (let frame = 0; frame < DRAW_CALL_SAMPLE_FRAMES; frame++) {
      if (signal?.aborted) return null
      applyFixture()
      if (!(await nextFrame(signal))) return null
      samples.push(drawCallTotal(app))
    }
    const artifact = summarizeDrawCalls(
      samples,
      new Date().toISOString(),
      navigator.userAgent,
      {
        ...drawCallBreakdown(app),
        ...sceneBatchDiagnostics(app),
        ...sceneMainCameraFrustumDiagnostics(app),
      },
      baseline,
      hqBaseline,
    )
    window.__ARENA_DRAW_CALL_RESULT__ = artifact
    console.info('[draw-call-harness]', JSON.stringify(artifact))
    if (!artifact.passed) {
      console.error(
        `[draw-call-harness] max ${artifact.max} must be < ${DRAW_CALL_BUDGET}`,
      )
    }
    return artifact
  } finally {
    // Do not clobber a newer live projection if auth/polling replaced the
    // fixture while the harness was running.
    const currentRuns = useArenaStore.getState().runs
    if (
      previous
      && (
        (runs !== undefined && currentRuns === runs)
        || (baselineRuns !== undefined && currentRuns === baselineRuns)
      )
    ) {
      useArenaStore.setState({
        runs: previous.runs,
        agents: previous.agents,
      })
    }
    if (view && previousPose) view.restore(previousPose)
    // An aborted StrictMode mount may be replaced while its asset promise is
    // still settling. Its eventual cleanup must not unlock the replacement.
    if (activeHarness?.token === token) activeHarness = undefined
  }
}
